import { createFileRoute, redirect } from '@tanstack/react-router';
import { CheckCircle2Icon, Share2Icon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useGetMetaIntegration } from '@/features/integrations/api';
import { MetaConnectSelectionDialog } from '@/features/integrations/components';
import { useMetaLoginForBusiness } from '@/features/integrations/hooks/use-meta-login-for-business';
import { authRedirectSearchValue } from '@/lib/auth-redirect';
import { ensureSession } from '@/lib/session';
import { ConnectRouteError } from './-connect-route-error';

/**
 * Browser hand-off target for connecting Facebook & Instagram (Facebook Login
 * for Business) from the mobile app.
 *
 * Same constraint as /connect/whatsapp: the FLfB popup runs through the
 * Facebook JS SDK, which only works from a domain on the Meta app's JS-SDK
 * allowlist — never the Capacitor WebView's `https://localhost` origin. The
 * native "Connect" button opens the system browser here; once done the user
 * closes the browser and the app refreshes its integration state on resume.
 */
export const Route = createFileRoute('/connect/facebook')({
  beforeLoad: async ({ location }) => {
    const session = await ensureSession();

    if (session.unavailable) {
      throw new Error('Could not load your session');
    }

    if (!session.user) {
      throw redirect({
        to: '/sign-in',
        search: { redirect: authRedirectSearchValue(location) },
      });
    }

    if (!session.user.organizationId) {
      throw redirect({ to: '/onboarding' });
    }
  },
  errorComponent: ConnectRouteError,
  component: ConnectFacebookPage,
});

function ConnectFacebookPage() {
  const [justConnected, setJustConnected] = useState(false);
  // The popup only stores what the login can reach; the connection is done
  // once the owner has picked the business, ad account and Page here.
  const [isSelectingAssets, setIsSelectingAssets] = useState(false);
  const { integration } = useGetMetaIntegration();
  const { launch, isReady, isBusy } = useMetaLoginForBusiness({
    onSuccess: () => setIsSelectingAssets(true),
  });

  return (
    <>
      <title>Connect Facebook | Borrah</title>
      <div className="mx-auto flex min-h-svh max-w-lg items-center px-4">
        <Card className="w-full">
          <CardContent className="space-y-4 pt-6">
            {justConnected ? (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="text-primary size-5" />
                  <h1 className="text-lg font-semibold">
                    Facebook &amp; Instagram are connected
                  </h1>
                </div>
                <p className="text-muted-foreground text-sm">
                  You&rsquo;re all set. Close this window to return to the
                  Borrah app — your connection will show up there automatically.
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Share2Icon className="text-primary size-5" />
                  <h1 className="text-lg font-semibold">
                    Connect Facebook &amp; Instagram
                  </h1>
                </div>
                <p className="text-muted-foreground text-sm">
                  Continue with Facebook to link your Facebook Page, Instagram
                  account, and ad account. A Facebook window will open — sign in
                  if asked, pick your business, and follow the steps.
                </p>
                {integration && (
                  <p className="text-muted-foreground rounded-lg border p-3 text-xs">
                    A Meta connection already exists for this organisation.
                    Continuing will reconnect it.
                  </p>
                )}
                <Button
                  type="button"
                  className="w-full"
                  onClick={launch}
                  disabled={!isReady || isBusy}
                >
                  {isBusy ? 'Connecting…' : 'Continue with Facebook'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <MetaConnectSelectionDialog
        open={isSelectingAssets}
        onOpenChange={setIsSelectingAssets}
        onSuccess={() => {
          setIsSelectingAssets(false);
          setJustConnected(true);
        }}
      />
    </>
  );
}
